import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  Button,
  Input,
  Form,
  Row,
  Col,
  Divider,
  Select,
  message,
  Modal,
  Card,
  Space,
  Typography,
} from "antd";
import {
  fetchUserDetailsByEmail,
  saveTransferOwnership,
  createPhoenixUser,
} from "../../../../../../apis/orderDetails";
import {
  createUserGraphQL,
  searchUsersByEmail,
  searchUsersByMobile,
  searchUsersByEmailOrMobile,
  searchUsersByMobileWithIsd,
  searchUsersByEmailOrMobileWithIsd,
} from "../../../../../../apis/graphqlService";
import { COUNTRY_PHONE_CODE } from "../../../../../phone-constants";
import styles from "../index.scss";
import getMessageFromResponse from "utils/getMessageFromResponse";
import { useSelector } from "react-redux";
import Toast from "widgets/Toast";
import UserDetailsCard from "../../UserInfoTableKH/components/UserDetailsCard";
import UserSelectionModal from "../../GroupOrder/components/UserSelectionModal";
import {
  UserAddOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const toast = Toast();

function fixSvgFlag(svgString) {
  return svgString
    .replace(/width="[^"]*"/g, "")
    .replace(/height="[^"]*"/g, "")
    .replace(
      /<svg/,
      '<svg style="width:20px;height:14px;display:inline-block;vertical-align:middle;margin-right:6px;max-width:20px;max-height:14px;"'
    );
}

const TransferOwnershipDrawer = ({
  visible,
  onClose,
  billingRequestId,
  tenantCode,
  transferHistory,
  sourceUserId,
  programKey,
  currency,
  applicationBatch,
  onTransferSuccess,
}) => {
  const isOwnerShipTransferred =
    transferHistory?.transferType === "OWNERSHIP_TRANSFER";
  const transferOwnershipData = transferHistory;
  const [fetchingUserDetails, setFetchingUserDetails] = useState(false);
  const [savingTransfer, setSavingTransfer] = useState(false);
  const [userDetailsFetched, setUserDetailsFetched] = useState(false);
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentMobile, setCurrentMobile] = useState("");
  const [currentIsd, setCurrentIsd] = useState("+91");
  const [emailSearched, setEmailSearched] = useState(false);
  const [mobileSearched, setMobileSearched] = useState(false);
  const [userNotFound, setUserNotFound] = useState(false);
  const previousUserDetailsRef = useRef(null);
  const currentTenant = useSelector((state) => state.common.currentTenant);

  // User creation states
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserForm] = Form.useForm();

  // GraphQL user search states
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setUserDetailsFetched(false);
      setCurrentEmail("");
      setCurrentMobile("");
      setCurrentIsd("+91");
      setEmailSearched(false);
      setMobileSearched(false);
      setUserNotFound(false);
      previousUserDetailsRef.current = null;
    }
  }, [visible, form]);

  const handleClose = () => {
    onClose();
  };

  const handleSearchUser = async () => {
    const email = currentEmail.trim();
    const mobile = currentMobile.trim();
    const isd = currentIsd;

    if (!email || !mobile) {
      message.error("Please enter both email and mobile number");
      return;
    }

    setSearchLoading(true);
    setEmailSearched(!!email);
    setMobileSearched(!!mobile);

    try {
      // Search by both email and mobile with ISD (both are now mandatory)
      const searchResponse = await searchUsersByEmailOrMobileWithIsd(
        email,
        mobile,
        isd
      );

      // Check if GraphQL search returned results
      if (
        searchResponse &&
        searchResponse.result &&
        searchResponse.result.length > 0
      ) {
        // Users found, show selection modal
        setSearchResults(searchResponse.result);
        setShowUserSelectionModal(true);
        setUserNotFound(false);
      } else if (
        searchResponse &&
        Array.isArray(searchResponse) &&
        searchResponse.length > 0
      ) {
        // Handle case where response is directly an array
        setSearchResults(searchResponse);
        setShowUserSelectionModal(true);
        setUserNotFound(false);
      } else {
        // No users found
        setUserDetailsFetched(false);
        setUserNotFound(true);
        // Reset fields except search fields
        form.setFieldsValue({
          firstName: "",
          lastName: "",
          phoneNumber: "",
          email: "",
          destinationUserId: "",
        });
        message.warning("User not found. You can create a new user.");
      }
    } catch (error) {
      console.error("Error searching user:", error);
      setUserDetailsFetched(false);
      setUserNotFound(true);
      message.error("Failed to search for users. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserSelect = async (selectedUser) => {
    try {
      // Transform GraphQL user data to match expected format
      const userData = {
        id: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        email: selectedUser.email,
        phoneNumber: selectedUser.mobile || "",
        mobile: selectedUser.mobile || "",
        iamAuthId: selectedUser.iamAuthId,
        isEmailVerified: selectedUser.isEmailVerified,
        isMobileVerify: selectedUser.isMobileVerify,
        isd: selectedUser.isd || "+91",
        country: "India", // Default to India, can be made dynamic if country data is available
        website: "",
      };

      // Call Phoenix user creation API and get the response
      const phoenixResponse = await createPhoenixUser(userData);

      // Use the Phoenix user ID from the response
      const phoenixUserId = phoenixResponse.id;

      // Update form with selected user data, using Phoenix user ID
      form.setFieldsValue({
        firstName: userData.firstName,
        lastName: userData.lastName || "",
        phoneNumber: userData.phoneNumber,
        email: userData.email, // Add email to form
        destinationUserId: phoenixUserId, // Use Phoenix user ID instead of GraphQL ID
      });

      // Update current email state
      setCurrentEmail(userData.email);

      setUserDetailsFetched(true);
      setUserNotFound(false);
      setShowUserSelectionModal(false);
      setSearchResults([]);

      message.success(
        `User "${userData.firstName} ${userData.lastName}" selected successfully`
      );
    } catch (error) {
      console.error("Error selecting user:", error);
      message.error("Failed to select user. Please try again.");
    }
  };

  const handleCreateUser = async () => {
    try {
      const values = await createUserForm.validateFields();
      setCreatingUser(true);

      // Trim input values
      const trimmedValues = {
        firstName: values.firstName?.trim() || "",
        lastName: values.lastName?.trim() || "",
        email: values.email?.trim() || "",
        phoneNumber: values.phoneNumber?.trim() || "",
        isd: values.isd,
      };

      // Create user using GraphQL mutation
      const createdUser = await createUserGraphQL({
        firstName: trimmedValues.firstName,
        lastName: trimmedValues.lastName,
        email: trimmedValues.email,
        phoneNumber: trimmedValues.phoneNumber,
        isd: trimmedValues.isd, // Include ISD code
      });

      if (createdUser) {
        // Call Phoenix user creation API and get the response
        const phoenixResponse = await createPhoenixUser({
          id: createdUser.id,
          firstName: createdUser.firstName,
          lastName: createdUser.lastName || "",
          email: createdUser.email,
          mobile: createdUser.mobile,
          isd: trimmedValues.isd, // Use the selected ISD code
          isEmailVerified: false,
          isMobileVerify: false,
        });

        // Use the Phoenix user ID from the response
        const phoenixUserId = phoenixResponse.id;

        message.success("User created successfully!");

        // Update the form with the created user details, using Phoenix user ID
        form.setFieldsValue({
          firstName: createdUser.firstName,
          lastName: createdUser.lastName || "",
          phoneNumber: createdUser.mobile,
          email: createdUser.email, // Add email to form
          destinationUserId: phoenixUserId, // Use Phoenix user ID instead of GraphQL ID
        });

        // Update current email state
        setCurrentEmail(createdUser.email);

        setUserDetailsFetched(true);
        setUserNotFound(false);
        setShowCreateUserModal(false);
        createUserForm.resetFields();
      }
    } catch (error) {
      console.error("Error creating user:", error);
      message.error("Failed to create user. Please try again.");
    } finally {
      setCreatingUser(false);
    }
  };

  const openCreateUserModal = () => {
    const email = currentEmail;
    const mobile = currentMobile;

    createUserForm.setFieldsValue({
      email: email,
      firstName: "",
      lastName: "",
      phoneNumber: mobile || "", // Populate phone number if searched by mobile
      isd: currentIsd, // Set current ISD code
    });
    setShowCreateUserModal(true);
  };

  // Reset the email search state to allow searching a new email
  const handleEmailChange = (event) => {
    const emailValue = event.target.value;
    setCurrentEmail(emailValue);

    if (emailSearched) {
      setEmailSearched(false);
      setUserNotFound(false);
      setUserDetailsFetched(false);
    }

    // If email is cleared, also clear all other user fields
    if (!emailValue || emailValue.trim() === "") {
      form.setFieldsValue({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        destinationUserId: "",
      });
    }
  };

  // Reset the mobile search state to allow searching a new mobile
  const handleMobileChange = (event) => {
    const mobileValue = event.target.value;
    setCurrentMobile(mobileValue);

    if (mobileSearched) {
      setMobileSearched(false);
      setUserNotFound(false);
      setUserDetailsFetched(false);
    }

    // If mobile is cleared, also clear all other user fields
    if (!mobileValue || mobileValue.trim() === "") {
      form.setFieldsValue({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        destinationUserId: "",
      });
    }
  };

  const handleIsdChange = (value) => {
    setCurrentIsd(value);
  };

  const handleSaveTransfer = async () => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      if (!userDetailsFetched) {
        form.setFields([
          {
            name: "email",
            errors: ['Please click on "Fetch Details" button first'],
          },
        ]);
        return;
      }

      if (!values.destinationUserId) {
        toast.error("Please fetch user details first");
        return;
      }

      const userData = {
        billingRequestId: billingRequestId,
        sourceUserId: sourceUserId,
        destinationUserId: values.destinationUserId,
        currency: currency,
        programKey: programKey,
        batchId: applicationBatch,
        tenant: currentTenant?.code,
      };

      setSavingTransfer(true);
      const response = await saveTransferOwnership(userData);
      if (response.status === 200) {
        toast.success("Ownership transferred successfully");
        handleClose();
        onTransferSuccess();
      } else {
        toast.error("Failed to transfer ownership");
      }
      setSavingTransfer(false);
    } catch (error) {
      console.error("Error saving transfer ownership:", error);
      const errorMessage = getMessageFromResponse(error);
      toast.error(errorMessage);
      setSavingTransfer(false);
    }
  };

  const renderTransferredContent = () => {
    if (!transferOwnershipData)
      return <div>No ownership transfer data found</div>;

    return (
      <div>
        <UserDetailsCard
          userDetails={transferOwnershipData.targetUser}
          title="New Ownership"
        />
        <Divider />
        <UserDetailsCard
          userDetails={transferOwnershipData.sourceUser}
          title="Old Ownership"
        />
      </div>
    );
  };

  const isFetchButtonDisabled = () => {
    const emailTrimmed = currentEmail.trim();
    const mobileTrimmed = currentMobile.trim();
    return (
      !emailTrimmed ||
      !mobileTrimmed ||
      searchLoading ||
      (emailTrimmed === previousUserDetailsRef.current?.email &&
        mobileTrimmed === previousUserDetailsRef.current?.mobile &&
        userDetailsFetched)
    );
  };

  const isSaveButtonDisabled = () => {
    return (
      !userDetailsFetched || !form.getFieldValue("firstName") || savingTransfer
    );
  };

  const renderTransferForm = () => {
    return (
      <div style={{ padding: "0 16px" }}>
        <Form form={form} layout="vertical">
          <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
            <Col xs={24} sm={6} md={6} lg={6}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Email Address *</span>}
                name="searchEmail"
                rules={[
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter email address"
                  value={currentEmail}
                  onChange={handleEmailChange}
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col xs={8} sm={5} md={4} lg={4}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>ISD Code *</span>}
                name="searchIsd"
                style={{ marginBottom: 0 }}
              >
                <Select
                  showSearch
                  placeholder="ISD"
                  value={currentIsd}
                  onChange={handleIsdChange}
                  optionFilterProp="value"
                  filterOption={(input, option) => {
                    // Only allow numeric search (including + symbol)
                    const searchValue = input.replace(/[^0-9+]/g, "");
                    const optionValue = option.value || "";
                    return optionValue.indexOf(searchValue) >= 0;
                  }}
                  size="middle"
                >
                  {Object.values(COUNTRY_PHONE_CODE).map((item) => (
                    <Select.Option key={item.code} value={item.dialCode}>
                      <span
                        className="isd-flag"
                        dangerouslySetInnerHTML={{
                          __html: fixSvgFlag(item.flag),
                        }}
                      />
                      {item.dialCode}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={16} sm={7} md={7} lg={7}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Mobile Number *</span>}
                name="searchMobile"
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="Enter mobile ..."
                  value={currentMobile}
                  onChange={handleMobileChange}
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={6}
              md={4}
              lg={4}
              style={{ display: "flex", alignItems: "end" }}
            >
              <Form.Item
                label={<span>&nbsp;</span>}
                colon={false}
                style={{ marginBottom: 0 }}
              >
                <Button
                  type="primary"
                  loading={searchLoading}
                  onClick={handleSearchUser}
                  icon={<SearchOutlined />}
                  size="middle"
                  style={{ width: "100%" }}
                  disabled={!currentEmail.trim() || !currentMobile.trim()}
                >
                  Search
                </Button>
              </Form.Item>
            </Col>
          </Row>

          {userNotFound && (
            <div className={styles["user-not-found-alert"]}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong style={{ color: "#d48806" }}>
                  User not found
                </Text>
                <Text type="secondary">
                  No user found with the provided details. You can create a new
                  user instead.
                </Text>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={openCreateUserModal}
                  size="middle"
                >
                  Create New User
                </Button>
              </Space>
            </div>
          )}
        </Form>

        {/* User Details Section (keep as is, but remove card) */}
        <div style={{ marginTop: 24, marginBottom: 24 }}>
          <Form form={form} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: "Please enter first name" },
                  ]}
                >
                  <Input
                    placeholder="Enter first name"
                    disabled={!userDetailsFetched}
                    size="middle"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Last Name" name="lastName">
                  <Input
                    placeholder="Enter last name"
                    disabled={!userDetailsFetched}
                    size="middle"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[
                    { required: true, message: "Please enter email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input
                    placeholder="Enter email address"
                    disabled={!userDetailsFetched}
                    size="middle"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Phone Number"
                  name="phoneNumber"
                  rules={[
                    { required: true, message: "Please enter phone number" },
                  ]}
                >
                  <Input
                    placeholder="Enter phone number"
                    disabled={!userDetailsFetched}
                    size="middle"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="destinationUserId" hidden>
              <Input />
            </Form.Item>
          </Form>
        </div>

        <div className={styles["transfer-actions"]}>
          <Button onClick={handleClose} size="large">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSaveTransfer}
            loading={savingTransfer}
            disabled={isSaveButtonDisabled()}
            size="large"
          >
            Transfer Ownership
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title="Transfer Ownership"
      placement="right"
      onClose={handleClose}
      visible={visible}
      width="70%"
      footer={
        isOwnerShipTransferred ? (
          <div className={styles["transfer-ownership-drawer__footer"]}>
            <Button onClick={handleClose} type="primary">
              Close
            </Button>
          </div>
        ) : null
      }
    >
      <div>
        {isOwnerShipTransferred
          ? renderTransferredContent()
          : renderTransferForm()}
      </div>

      {/* User Selection Modal */}
      <UserSelectionModal
        visible={showUserSelectionModal}
        users={searchResults}
        onSelect={handleUserSelect}
        onCancel={() => {
          setShowUserSelectionModal(false);
          setSearchResults([]);
        }}
        loading={searchLoading}
      />

      {/* User Creation Modal */}
      <Modal
        title="Create New User"
        visible={showCreateUserModal}
        onCancel={() => setShowCreateUserModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowCreateUserModal(false)}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={creatingUser}
            onClick={handleCreateUser}
          >
            Create User
          </Button>,
        ]}
        width={500}
      >
        <Form form={createUserForm} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>

          <Form.Item name="lastName" label="Last Name">
            <Input placeholder="Enter last name" />
          </Form.Item>

          <Form.Item label="Phone Number" required>
            <Input.Group compact>
              <Form.Item
                name="isd"
                noStyle
                rules={[{ required: true, message: "Please select ISD code" }]}
                initialValue="+91"
              >
                <Select
                  showSearch
                  style={{ width: "25%" }}
                  placeholder="ISD"
                  optionFilterProp="value"
                  filterOption={(input, option) => {
                    // Only allow numeric search (including + symbol)
                    const searchValue = input.replace(/[^0-9+]/g, "");
                    const optionValue = option.value || "";
                    return optionValue.indexOf(searchValue) >= 0;
                  }}
                >
                  {Object.values(COUNTRY_PHONE_CODE).map((item) => (
                    <Select.Option key={item.code} value={item.dialCode}>
                      <span
                        className="isd-flag"
                        dangerouslySetInnerHTML={{
                          __html: fixSvgFlag(item.flag),
                        }}
                      />
                      {item.dialCode}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                noStyle
                rules={[
                  { required: true, message: "Please enter phone number" },
                ]}
              >
                <Input
                  style={{ width: "75%" }}
                  placeholder="Enter phone number"
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
};

TransferOwnershipDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  billingRequestId: PropTypes.string,
  tenantCode: PropTypes.string,
  transferHistory: PropTypes.object,
  sourceUserId: PropTypes.number,
  programKey: PropTypes.string,
  currency: PropTypes.string,
  applicationBatch: PropTypes.string,
  onTransferSuccess: PropTypes.func,
};

export default TransferOwnershipDrawer;
